/**
 * Schlanker fetch-Wrapper.
 *
 * Features:
 *  - Bearer-JWT-Header automatisch (aus auth-Store).
 *  - Auto-Logout bei HTTP 401.
 *  - Parsen von RFC-7807-Error-Bodies (application/problem+json).
 *  - Typ-sichere generische Methoden: api.get<T>(...), api.post<TRes, TBody>(...).
 *  - JSON-Body-Serialisierung, query-string-Helper.
 */

import { forceLogout, getAccessToken } from './auth';
import type { ProblemDetails } from '@/types/api';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '');

export class ApiError extends Error {
  public readonly status: number;
  public readonly problem: ProblemDetails | null;
  public readonly raw: unknown;

  constructor(status: number, problem: ProblemDetails | null, raw: unknown) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.raw = raw;
  }
}

export interface QueryParams {
  [key: string]: string | number | boolean | null | undefined;
}

export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    usp.append(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

interface RequestOptions {
  signal?: AbortSignal;
  /** Falls true, wird kein Bearer-Header angehängt (z.B. Login). */
  anonymous?: boolean;
  /** Zusätzliche Header. */
  headers?: Record<string, string>;
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body: unknown,
  opts: RequestOptions = {}
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...opts.headers,
  };

  if (!opts.anonymous) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const init: RequestInit = { method, headers };
  if (opts.signal) init.signal = opts.signal;

  if (body instanceof FormData) {
    // Content-Type von Browser setzen lassen (boundary).
    init.body = body;
  } else if (body !== undefined && body !== null) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    // Netzwerk / Abort
    throw new ApiError(0, null, err);
  }

  if (res.status === 401 && !opts.anonymous) {
    forceLogout();
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  const isJson =
    contentType.includes('application/json') || contentType.includes('application/problem+json');

  let payload: unknown = null;
  if (isJson) {
    try {
      payload = await res.json();
    } catch {
      payload = null;
    }
  } else {
    try {
      payload = await res.text();
    } catch {
      payload = null;
    }
  }

  if (!res.ok) {
    const problem = isProblem(payload) ? payload : null;
    throw new ApiError(res.status, problem, payload);
  }

  return payload as T;
}

function isProblem(value: unknown): value is ProblemDetails {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.title === 'string' && typeof v.status === 'number' && typeof v.code === 'string'
  );
}

export const api = {
  get: <T>(path: string, params?: QueryParams, opts?: RequestOptions) =>
    request<T>('GET', `${path}${buildQuery(params)}`, null, opts),
  post: <TRes, TBody = unknown>(path: string, body?: TBody, opts?: RequestOptions) =>
    request<TRes>('POST', path, body, opts),
  postForm: <TRes>(path: string, form: FormData, opts?: RequestOptions) =>
    request<TRes>('POST', path, form, opts),
  patch: <TRes, TBody = unknown>(path: string, body?: TBody, opts?: RequestOptions) =>
    request<TRes>('PATCH', path, body, opts),
  put: <TRes, TBody = unknown>(path: string, body?: TBody, opts?: RequestOptions) =>
    request<TRes>('PUT', path, body, opts),
  delete: <T = void>(path: string, opts?: RequestOptions) =>
    request<T>('DELETE', path, null, opts),
};

export const API_BASE_URL = API_BASE;
